import * as React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormGroup, ControlLabel, Col, Row } from 'react-bootstrap';
import DateTime from 'react-datetime';
import Loader from 'react-loader';
import { Translate } from '../../../shared/LanguageService';
import { handleError } from '../../../shared/ErrorCatching';
import * as actions from '../OrdersActions';
import { IPhotoOrderListItem } from '../Entities';
import FormSelect from '../../../components/common/FormSelect';
import { assign } from '../../../shared/Assign';
import * as PhotoStatus from '../../../shared/PhotoStatus';
import PhotoOrderList from './PhotoOrderList';
import FormInputChangeHandler from './../../../shared/form/FormInputChangeHandler';
import { filterByProduct, ProductFilterOption } from './productFilter';

const LIST_UPDATE_INTERVAL_MS: number = 600000;

class PhotoOrdersPage extends React.Component<any, any> {
    private formInputChangeHandler: FormInputChangeHandler;
    private pollingTimer: number;

    constructor(props: any, context: any) {
        super(props, context);
        this.state = {
            orders: [],
            filters: {
                product: ProductFilterOption.All,
                orderStatus: -1,
                region: -1,
                imageEditorFirm: -1,
                endDate: moment(new Date()),
                isLoading: false
            }
        };

        this.closeModal = this.closeModal.bind(this);
        this.setValue = this.setValue.bind(this);
        this.onDateTimeChange = this.onDateTimeChange.bind(this);
        this.formInputChangeHandler = new FormInputChangeHandler(this.state.filters, this.setValue);
    }

    setValue(newState: any) {
        this.setState({ filters: newState });
    }

    componentDidMount() {
        this.pollForOrders().catch(handleError);
    }

    componentWillUnmount() {
        clearTimeout(this.pollingTimer);
    }

    pollForOrders = async (): Promise<void> => {
        try {
            await this.getPhotoOrders();
            clearTimeout(this.pollingTimer);
            this.pollingTimer = window.setTimeout(this.pollForOrders, LIST_UPDATE_INTERVAL_MS);
        } catch (error) {
            throw error;
        }
    };

    getPhotoOrders = (): Promise<void> => {
        const { isLoading, filters } = this.state;
        const { actions } = this.props;

        if (isLoading) {
            return Promise.resolve();
        }

        const date = moment(filters.endDate);
        this.setState({ isLoading: true });
        return actions
            .getOrders(date.year(), date.month() + 1, date.date())
            .then(() => {
                this.setState({ isLoading: false });
            })
            .catch((error: any) => {
                this.setState({ isLoading: false });
                throw error;
            });
    };

    onDateTimeChange(moment: any): void {
        let filters = this.state.filters;
        filters.endDate = moment;

        this.setState({ filters: assign({}, filters) }, this.getPhotoOrders);
    }

    render() {
        let orders: IPhotoOrderListItem[] = [];
        if (this.props.orders) {
            orders = this.props.orders;
        }

        let filters = this.state.filters;
        orders = filterByProduct(orders, filters.product);

        if (filters.orderStatus !== -1) {
            if (filters.orderStatus === PhotoStatus.Delivered) {
                orders = orders.filter((x) => x.delivered);
            } else {
                orders = orders.filter(
                    (x) => x.orderStatus.toString() === filters.orderStatus.toString()
                );
            }
        }

        if (filters.orderStatus !== PhotoStatus.Delivered) {
            orders = orders.filter((x) => !x.delivered);
        }

        if (filters.region !== -1) {
            orders = orders.filter((x) => x.regionId.toString() === filters.region.toString());
        }

        if (filters.imageEditorFirm !== -1) {
            orders = orders.filter(
                (x) => x.imageEditorFirmId.toString() === filters.imageEditorFirm.toString()
            );
        }

        if (orders && filters.orderStatus !== PhotoStatus.Delivered) {
            orders = orders.filter(
                (x) =>
                    moment(x.deadline) < moment(filters.endDate).add(1, 'days').hour(0).minute(0) ||
                    x.isOldEditOrder
            );
            orders.sort((x) => x.deadline);

            let earlyDeliveryOrders = orders.filter((x) => x.isEarlyDelivery);
            orders = orders.filter((x) => !x.isEarlyDelivery);
            earlyDeliveryOrders.forEach((earlyDeliveryOrder) => {
                orders.splice(0, 0, earlyDeliveryOrder);
            });
        }

        return (
            <div>
                <Row>
                    <h1 className="col-xs-12">
                        {Translate('orders/title/photo')}{' '}
                        {!this.state.isLoading && (
                            <span className="number-of-orders">({orders.length})</span>
                        )}
                    </h1>
                </Row>
                <Row style={{ display: 'flex' }}>
                    <Col md={2} style={{ flexGrow: 1 }}>
                        <FormGroup className="no-margin">
                            <ControlLabel>
                                {Translate('orders/list/photo/header/date')}
                            </ControlLabel>
                            <DateTime
                                locale={moment.locale()}
                                timeFormat={false}
                                onChange={this.onDateTimeChange}
                                value={filters.endDate}
                                closeOnSelect={true}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={2} style={{ flexGrow: 1 }}>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('orders/list/photo/header/status')}
                            </ControlLabel>
                            <FormSelect
                                name="orderStatus"
                                data-testid="filter-orderStatus"
                                value={this.state.filters.orderStatus}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option value="-1">
                                    {Translate('products/page/select-label/filter')}
                                </option>
                                {this.props.orderStatuses &&
                                    this.props.orderStatuses.map((t: any) => {
                                        return (
                                            <option key={t.id} value={t.id}>
                                                {Translate(t.name)}
                                            </option>
                                        );
                                    })}
                            </FormSelect>
                        </FormGroup>
                    </Col>
                    <Col md={2} style={{ flexGrow: 1 }}>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('orders/list/photo/header/regions')}
                            </ControlLabel>
                            <FormSelect
                                name="region"
                                data-testid="filter-region"
                                value={this.state.filters.region}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option value="-1">
                                    {Translate('products/page/select-label/filter')}
                                </option>
                                {this.props.regions &&
                                    this.props.regions.map((t: any) => {
                                        return (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        );
                                    })}
                            </FormSelect>
                        </FormGroup>
                    </Col>
                    <Col md={2} style={{ flexGrow: 1 }}>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('orders/list/photo/header/image-editor-firms')}
                            </ControlLabel>
                            <FormSelect
                                name="imageEditorFirm"
                                value={this.state.filters.imageEditorFirm}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option value="-1">
                                    {Translate('products/page/select-label/filter')}
                                </option>
                                {this.props.imageEditorFirms &&
                                    this.props.imageEditorFirms.map((t: any) => {
                                        return (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        );
                                    })}
                            </FormSelect>
                        </FormGroup>
                    </Col>
                    <Col md={2} style={{ flexGrow: 1 }}>
                        <FormGroup>
                            <ControlLabel>
                                {Translate('orders/list/photo/header/image-product')}
                            </ControlLabel>
                            <FormSelect
                                name="product"
                                data-testid="filter-product"
                                value={this.state.filters.product}
                                onChange={this.formInputChangeHandler.onChange}
                            >
                                <option value={ProductFilterOption.All}>
                                    {Translate('products/page/select-label/filter')}
                                </option>
                                <option value={ProductFilterOption.Express}>
                                    {Translate('orders/list/photo/header/only-express')}
                                </option>
                                <option value={ProductFilterOption.ExpressWeekend}>
                                    {Translate('orders/list/photo/header/only-express-weekend')}
                                </option>
                                <option value={ProductFilterOption.Film}>
                                    {Translate('orders/list/photo/header/only-film')}
                                </option>
                                <option value={ProductFilterOption.Text}>
                                    {Translate('orders/list/photo/header/only-text')}
                                </option>
                                <option value={ProductFilterOption.Visualization}>
                                    {Translate('orders/list/photo/header/only-visualization')}
                                </option>
                            </FormSelect>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Loader loaded={!this.state.isLoading}>
                        <PhotoOrderList items={orders} user={this.props.user} />
                    </Loader>
                </Row>
            </div>
        );
    }

    closeModal() {
        this.setState({ showModal: false, selectedOrderId: null });
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        orders: state.orders.photoOrders.list,
        regions: state.orders.photoOrders.regions,
        imageEditorFirms: state.orders.photoOrders.imageEditorFirms,
        orderStatuses: state.orders.photoOrders.orderStatuses,
        languageKey: state.user.details.languageKey,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getOrders: (year: number, month: number, day: number) =>
                dispatch(actions.getPhotoOrders(year, month, day))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PhotoOrdersPage));
